import StorageServ from '@api/Firebase/StorageService/StorageServ';
import { getBase64MimeType } from '@utils/Base64';

// ============================================================================
// LAS FOTOS DEL CATÁLOGO VIVEN EN STORAGE, NUNCA DENTRO DEL DOCUMENTO.
//
// Medido el 2026-09-09: el catálogo pesaba 1,391 KB y **1,234 KB eran fotos en
// base64 metidas dentro de los docs de `dishes`** — 31 platillos con `data:…`
// (39.6 KB de media, el mayor 137 KB), mientras los otros 56 ya usaban Storage.
// Consecuencias que se pagaban en cada lectura:
//   · cada lectura de esos platillos transfería hasta 137 KB de más;
//   · la caché persistida de la web pesaba 1.37 MB, al borde de la cuota de
//     `localStorage` — y al llenarse la persistencia se apagaba EN SILENCIO;
//   · y sobre todo: hacía imposible el catálogo pre-armado en UN documento
//     (límite de Firestore: 1 MiB). Sin las fotos son ~163 KB.
//
// Regla, decisión de Irving (D1 de PLAN-CATALOGO-EN-UNA-LECTURA.md): **el doc
// guarda la URL de Storage y nada más.** Se aplica en el SERVICIO que escribe
// (`DishServ.saveDish`), no en el formulario, para que la cumpla cualquier
// camino: el modal, un script, o lo que venga después.
//
// El formulario sigue entregando un data URI porque es lo que el `<img>` del
// preview necesita para pintarse sin haber subido nada; la subida ocurre al
// guardar. Así no quedan archivos huérfanos si alguien cancela el modal.
// ============================================================================

/**
 * Prefijo del aviso cuando Storage falla. Lo reconoce la pantalla para tratar el
 * guardado como HECHO-con-advertencia: el platillo ya está en Firestore y
 * reintentar crearía un duplicado.
 */
export const PHOTO_UPLOAD_WARNING = 'FOTO_NO_SUBIDA:';

/** Convención heredada de los 56 platillos que ya estaban en Storage. */
export const DISH_PHOTO_FOLDER = 'products';

/** ¿Es una foto embebida (`data:image/...;base64,…`) que hay que subir? */
export const isEmbeddedPhoto = (photoSrc?: string): boolean => /^data:/i.test((photoSrc || '').trim());

/** Extensión a partir del mime del data URI. JPEG por defecto: es lo que produce el optimizador. */
export const getPhotoExtension = (photoSrc: string): string => {
    const mime = getBase64MimeType(photoSrc).toLowerCase();
    if (mime.includes('png')) return 'png';
    if (mime.includes('webp')) return 'webp';
    if (mime.includes('gif')) return 'gif';
    return 'jpg';
};

/** Ruta en Storage para la foto de un platillo. */
export const getDishPhotoPath = (dishId: string, photoSrc: string): string =>
    `${DISH_PHOTO_FOLDER}/${dishId}.${getPhotoExtension(photoSrc)}`;

/** El base64 pelado que espera `uploadString(..., 'base64')` (sin el prefijo `data:`). */
export const stripDataUriPrefix = (photoSrc: string): string => photoSrc.split(',')[1] || '';

/**
 * Deja `photoSrc` listo para escribirse en Firestore: si viene embebido lo sube
 * a Storage y devuelve su URL; si ya es una URL, o está vacío, lo devuelve tal
 * cual (no vuelve a subir nada).
 *
 * **Nunca lanza.** Si Storage falla, devuelve el valor original y lo dice en
 * `uploaded: false` con el error: quien guarda decide si avisar. Perder el
 * guardado del platillo por un fallo de Storage sería peor que guardar la foto
 * embebida una vez más.
 */
export const resolveDishPhoto = async (
    dishId: string,
    photoSrc?: string
): Promise<{ photoSrc: string; uploaded: boolean; error?: string }> => {
    const value = (photoSrc || '').trim();
    if (!isEmbeddedPhoto(value)) return { photoSrc: value, uploaded: false };
    try {
        const url = await StorageServ.uploadFileToStorage(
            getDishPhotoPath(dishId, value),
            stripDataUriPrefix(value),
            getBase64MimeType(value)
        );
        return { photoSrc: url, uploaded: true };
    } catch (error) {
        console.error('No se pudo subir la foto del platillo a Storage', error);
        return { photoSrc: value, uploaded: false, error: String(error) };
    }
};
